/**
 * Janitorial / EVS Catalog Overview — /marketplace/janitorial
 * Lists the 11 top-level categories with hero images + product counts.
 */

import { Link } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, Microscope, FlaskConical, Droplets, Sparkles,
  Wind, Bot, ShieldCheck, Wrench, Truck, Brush, Zap, Phone,
  ShoppingBag, ArrowRight, Leaf,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { JANITORIAL_CATEGORIES, JANITORIAL_PRODUCTS } from "@/data/janitorialCatalog";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";

const ICONS = { Microscope, FlaskConical, Droplets, Sparkles, Wind, Bot, ShieldCheck, Wrench, Truck, Brush, Zap };

const ACCENT_BG = {
  cyan: "from-cyan-500 to-cyan-700", amber: "from-amber-500 to-amber-700",
  blue: "from-blue-500 to-blue-700", indigo: "from-indigo-500 to-indigo-700",
  slate: "from-slate-500 to-slate-700", purple: "from-purple-500 to-purple-700",
  rose: "from-rose-500 to-rose-700", teal: "from-teal-500 to-teal-700",
  emerald: "from-emerald-500 to-emerald-700", orange: "from-orange-500 to-orange-700",
  fuchsia: "from-fuchsia-500 to-fuchsia-700",
};

// Green Klean® bottle image (self-hosted, scraped from gogreenklean.com)
const GK_BANNER_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/static/green-kleen/images/gk-tablets-bottle.jpg`;
// Filmop USA workstation image (same source as the Filmop section in HospitalMarketplace)
const FILMOP_BANNER_IMG = "https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif";

export default function JanitorialCatalogPage({ basePath = "/marketplace/janitorial" }) {
  const { setShowCart, cart } = useQuoteCart();
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="janitorial-catalog-page">
      <MarketplaceCategoryNav currentCategory="janitorial-evs" />
      <MarketplaceBreadcrumb trail={[{ label: "Janitorial & EVS" }]} />
      {/* Top bar */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to={basePath.startsWith("/hospital") ? "/hospital/marketplace" : "/marketplace"}
            className="flex items-center gap-2 text-slate-300 hover:text-white"
            data-testid="janitorial-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to Marketplace</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:2814563458" className="hidden sm:flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
              <Phone className="w-4 h-4" /> 281.456.3458
            </a>
            <Button
              onClick={() => setShowCart(true)}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold relative"
              data-testid="janitorial-open-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              Quote Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="janitorial-cart-count">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
          <Badge className="bg-cyan-500/20 text-cyan-200 border border-cyan-300/30 mb-4">
            Sterile Mate Marketplace · Janitorial / EVS
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 leading-[1.1]">
            Janitorial &amp; Environmental Service Supplies
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            ATP cleanliness verification, commercial cleaning chemicals, dilution control, restroom care,
            critical-filter vacuums, autonomous floor scrubbers, and the CleanSite Power 2-Punch system —
            sourced and quoted through Sterile Mate.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs text-slate-400">
            <span><strong className="text-white">{JANITORIAL_CATEGORIES.length}</strong> categories</span>
            <span>·</span>
            <span><strong className="text-white">{JANITORIAL_PRODUCTS.length}</strong> products</span>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {JANITORIAL_CATEGORIES.map(cat => {
            const Icon = ICONS[cat.icon] || Sparkles;
            const productCount = JANITORIAL_PRODUCTS.filter(p => p.category === cat.slug).length;
            return (
              <Link
                key={cat.slug}
                to={`${basePath}/${cat.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-emerald-400 hover:shadow-xl transition-all"
                data-testid={`janitorial-cat-${cat.slug}`}
              >
                {/* Clean product image on a light backdrop — no gradient/blend tricks */}
                <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
                  <img
                    src={cat.hero}
                    alt={cat.name}
                    className="absolute inset-0 w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  {/* Icon chip — small, pill, colored — sits on top-left */}
                  <div className={`absolute top-3 left-3 w-10 h-10 rounded-xl bg-gradient-to-br ${ACCENT_BG[cat.accent] || "from-slate-500 to-slate-700"} flex items-center justify-center shadow-md`}>
                    <Icon className="w-5 h-5 text-white" strokeWidth={2.25} />
                  </div>
                  <div className="absolute top-3 right-3 bg-white/95 backdrop-blur text-slate-900 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                    {productCount} {productCount === 1 ? "product" : "products"}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 text-lg leading-tight mb-1.5 group-hover:text-emerald-700 transition-colors">{cat.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{cat.short}</p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Cross-link banners — Green Klean® + Filmop USA (with real product imagery) */}
        <div className="mt-10 grid md:grid-cols-2 gap-6" data-testid="janitorial-cross-link-banners">
          {/* Green Klean® banner */}
          <Link
            to={basePath.startsWith("/hospital") ? "/hospital/marketplace/green-klean" : "/marketplace/green-klean"}
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })}
            data-testid="janitorial-greenklean-banner"
            className="group bg-white rounded-2xl overflow-hidden border-2 border-emerald-200 hover:border-emerald-500 hover:shadow-2xl transition-all flex flex-col"
          >
            {/* Real product image — same treatment as the category cards above */}
            <div className="relative aspect-[16/9] bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
              <img
                src={GK_BANNER_IMG}
                alt="Green Klean® Chlorinated Disinfecting Tablets"
                className="absolute inset-0 w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                data-testid="greenklean-banner-img"
              />
              <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                <Leaf className="w-5 h-5 text-white" strokeWidth={2.25} />
              </div>
              <div className="absolute top-3 right-3 bg-amber-300 text-emerald-900 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
                Featured Brand
              </div>
            </div>
            <div className="p-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Green Klean®</div>
                <h3 className="font-bold text-slate-900 text-lg leading-tight mb-1 group-hover:text-emerald-700 transition-colors">
                  Eco-Hospital-Grade Disinfectant Tablets
                </h3>
                <p className="text-sm text-slate-600 leading-snug">
                  EPA-registered chlorine tabs · 4-min C. diff kill · ships air-freight · vacuum bags &amp; filters available.
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-700 font-bold text-sm whitespace-nowrap group-hover:translate-x-1 transition-transform flex-shrink-0">
                Browse <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>

          {/* Filmop USA banner */}
          <Link
            to={basePath.startsWith("/hospital") ? "/hospital/marketplace" : "/hospital/marketplace"}
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })}
            data-testid="janitorial-filmop-banner"
            className="group bg-white rounded-2xl overflow-hidden border-2 border-blue-200 hover:border-blue-500 hover:shadow-2xl transition-all flex flex-col"
          >
            <div className="relative aspect-[16/9] bg-gradient-to-br from-blue-50 to-cyan-50 overflow-hidden">
              <img
                src={FILMOP_BANNER_IMG}
                alt="Filmop USA Alpha EVS Workstation"
                className="absolute inset-0 w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                data-testid="filmop-banner-img"
              />
              <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center shadow-md">
                <ShoppingBag className="w-5 h-5 text-white" strokeWidth={2.25} />
              </div>
              <div className="absolute top-3 right-3 bg-cyan-300 text-blue-900 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
                Partner Catalog
              </div>
            </div>
            <div className="p-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-blue-700 mb-1">Filmop USA</div>
                <h3 className="font-bold text-slate-900 text-lg leading-tight mb-1 group-hover:text-blue-700 transition-colors">
                  Mops, Microfiber &amp; EVS Carts
                </h3>
                <p className="text-sm text-slate-600 leading-snug">
                  Color-coded mop systems, ergonomic buckets, infection-control microfiber, and Alpha workstations.
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-blue-700 font-bold text-sm whitespace-nowrap group-hover:translate-x-1 transition-transform flex-shrink-0">
                Browse <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* SOURCE CALLOUT */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-emerald-900">
            Need help selecting the right product? Our team will quote and consult — no markup until ordered.
          </div>
          <a
            href="tel:2814563458"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
            data-testid="janitorial-call-link"
          >
            <Phone className="w-3.5 h-3.5" /> 281.456.3458
          </a>
        </div>
      </section>
    </div>
  );
}
